/**
 * The Glass — stewardship pane derivations (wave glass-7, #807).
 *
 * Pure functions beside the pane (the catalog-search.ts precedent): pane
 * behavior, not canon. Everything here is a MECHANICAL regrouping of the
 * packaged canon — the ratification marks are the generator's declared
 * derivation (phrase presence, see generate-glass-canon.mjs); this module
 * only sorts entries into the four measured mark groups and joins route
 * refs against the already-packaged register rows. No judgment is added:
 * group names name the measurement, labels quote the files.
 */

import type {
  GlassActivenessSurface,
  GlassRatificationMark,
  GlassRegisterRow,
} from "@command-center/shared";

/** The four measured mark groups — names name the MEASUREMENT (which
 *  phrases the entry's warrant + presence_exempt text carries), never a
 *  state of the world. */
export type MarkGroupId =
  | "ratified-only"
  | "pending-phrase-only"
  | "both"
  | "neither";

export const MARK_GROUP_ORDER: readonly MarkGroupId[] = [
  "pending-phrase-only",
  "both",
  "ratified-only",
  "neither",
];

/** The pending-phrase measurement as the generator DECLARES it
 *  (generate-glass-canon.mjs header and its PENDING_PHRASE_RE): pendingPhrase
 *  is the first match of this pattern in the entry's warrant +
 *  presence_exempt text, carried verbatim. Named once here so every label
 *  references the declared pattern rather than one literal phrase. */
export const PENDING_PHRASE_PATTERN = String.raw`/[Rr]atify(?: repoint)? at \/?pause/`;

/** What each group MEASURES — the labels the pane renders beside the group
 *  headings. Phrase-presence descriptions, not verdicts. */
export const MARK_GROUP_MEASURE: Record<MarkGroupId, string> = {
  "pending-phrase-only": `warrant/presence_exempt text matches the generator-declared ratify-at-pause pattern ${PENDING_PHRASE_PATTERN} and carries no "RATIFIED"`,
  both: `text matches the generator-declared ratify-at-pause pattern ${PENDING_PHRASE_PATTERN} and carries "RATIFIED"`,
  "ratified-only": `text carries "RATIFIED" and does not match the generator-declared ratify-at-pause pattern ${PENDING_PHRASE_PATTERN}`,
  neither: `text carries no "RATIFIED" and does not match the generator-declared ratify-at-pause pattern ${PENDING_PHRASE_PATTERN}`,
};

export function markGroup(mark: GlassRatificationMark): MarkGroupId {
  if (mark.hasRatified && mark.pendingPhrase !== null) return "both";
  if (mark.hasRatified) return "ratified-only";
  if (mark.pendingPhrase !== null) return "pending-phrase-only";
  return "neither";
}

export interface MarkedSurface {
  surface: GlassActivenessSurface;
  mark: GlassRatificationMark;
}

/** Surfaces joined to their marks and grouped, file order preserved within
 *  each group. A surface whose id has no mark (or vice versa) throws — the
 *  generator derives marks 1:1 from the surfaces, so a mismatch is a
 *  packaging defect, never something to render around. */
export function groupSurfaces(
  surfaces: readonly GlassActivenessSurface[],
  marks: readonly GlassRatificationMark[],
): Map<MarkGroupId, MarkedSurface[]> {
  const byId = new Map<string, GlassRatificationMark>();
  for (const m of marks) byId.set(m.id, m);
  const groups = new Map<MarkGroupId, MarkedSurface[]>();
  for (const g of MARK_GROUP_ORDER) groups.set(g, []);
  for (const s of surfaces) {
    const mark = byId.get(s.id);
    if (mark === undefined) {
      throw new Error(`no ratification mark packaged for surface "${s.id}"`);
    }
    const bucket = groups.get(markGroup(mark));
    if (bucket === undefined) throw new Error("unreachable: group order");
    bucket.push({ surface: s, mark });
  }
  return groups;
}

export interface RouteRegisterLine {
  id: string;
  ref: string;
  /** The register row whose path equals the route's ref, or null — null is
   *  the measured miss: no row at this exact path in the packaged
   *  register. */
  row: GlassRegisterRow | null;
}

/** Every surface carrying a route block, joined to the register by exact
 *  ref-path equality — the covered-vs-findable join, from already-packaged
 *  canon only. */
export function routeRegisterLines(
  surfaces: readonly GlassActivenessSurface[],
  rowsByPath: ReadonlyMap<string, GlassRegisterRow>,
): RouteRegisterLine[] {
  const lines: RouteRegisterLine[] = [];
  for (const s of surfaces) {
    if (s.route === undefined) continue;
    lines.push({ id: s.id, ref: s.ref, row: rowsByPath.get(s.ref) ?? null });
  }
  return lines;
}
